// WebSocket client for real-time assessment status updates

import { getAccessToken } from "./client";
import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

export type AssessmentStatus =
  | "pending"
  | "uploading"
  | "processing"
  | "draft_ready"
  | "under_review"
  | "signed_off"
  | "error";

export interface StatusUpdate {
  type: "status_update";
  assessment_id: string;
  status: AssessmentStatus;
  message: string;
}

type MessageHandler = (update: StatusUpdate) => void;

// Derive the WS base from the same VITE_API_URL the REST client uses, so one
// env var configures both. Falls back to localhost for dev when it's unset.
// VITE_WS_URL still wins if explicitly set.
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const WS_URL =
  import.meta.env.VITE_WS_URL ||
  `${API_BASE_URL.replace(/^http/, "ws").replace(/\/+$/, "")}/ws/assessments`;

// One socket per assessment, shared by every subscriber to that assessment and
// reference-counted so the socket only closes when the LAST subscriber leaves.
// The old design kept a single global socket keyed to one assessmentId, so a
// second component mounting (or a React StrictMode double-mount) tore down the
// first socket and silently stopped updates for the earlier subscriber.
class AssessmentConnection {
  private ws: WebSocket | null = null;
  private handlers: Set<MessageHandler> = new Set();
  private refCount = 0;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private closed = false;

  constructor(private readonly assessmentId: string) {}

  private open(): void {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }
    this.isConnecting = true;
    this.closed = false;

    const token = getAccessToken();
    const wsUrl = `${WS_URL}/${this.assessmentId}/`;
    const url = token ? `${wsUrl}?token=${token}` : wsUrl;

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        console.log(`[WS] Connected to ${this.assessmentId}`);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as StatusUpdate;
          if (data.type === "status_update") {
            this.handlers.forEach((handler) => handler(data));
          }
        } catch (e) {
          console.error("[WS] Failed to parse message:", e);
        }
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        if (!this.closed) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        this.isConnecting = false;
        console.error("[WS] Error:", error);
      };
    } catch (e) {
      this.isConnecting = false;
      console.error("[WS] Failed to connect:", e);
    }
  }

  private attemptReconnect(): void {
    if (this.refCount > 0 && this.reconnectAttempts < this.maxReconnectAttempts && !this.isConnecting) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      console.log(`[WS] Reconnecting to ${this.assessmentId} in ${delay}ms (attempt ${this.reconnectAttempts})`);

      setTimeout(() => {
        if (this.refCount > 0 && !this.closed) {
          this.open();
        }
      }, delay);
    }
  }

  subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    this.refCount++;
    this.open();

    return () => {
      this.handlers.delete(handler);
      this.refCount--;
      if (this.refCount <= 0) {
        this.close();
      }
    };
  }

  private close(): void {
    this.closed = true;
    this.reconnectAttempts = this.maxReconnectAttempts;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  hasSubscribers(): boolean {
    return this.refCount > 0;
  }
}

class AssessmentWebSocketManager {
  private connections: Map<string, AssessmentConnection> = new Map();

  subscribe(assessmentId: string, handler: MessageHandler): () => void {
    let connection = this.connections.get(assessmentId);
    if (!connection) {
      connection = new AssessmentConnection(assessmentId);
      this.connections.set(assessmentId, connection);
    }

    const unsubscribe = connection.subscribe(handler);

    return () => {
      unsubscribe();
      const conn = this.connections.get(assessmentId);
      if (conn && !conn.hasSubscribers()) {
        this.connections.delete(assessmentId);
      }
    };
  }

  isConnected(assessmentId: string): boolean {
    return this.connections.get(assessmentId)?.isConnected() ?? false;
  }
}

export const assessmentWs = new AssessmentWebSocketManager();

// React hook for WebSocket
export function useAssessmentStatus(assessmentId: string | null) {
  const [status, setStatus] = useState<AssessmentStatus | null>(null);
  const [message, setMessage] = useState<string>("");
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();

  const handleMessage = useCallback(
    (update: StatusUpdate) => {
      setStatus(update.status);
      setMessage(update.message);
      queryClient.invalidateQueries({ queryKey: ["assessment", update.assessment_id] });
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!assessmentId) {
      return;
    }

    setStatus(null);
    setMessage("");

    const unsubscribe = assessmentWs.subscribe(assessmentId, handleMessage);
    setIsConnected(true);

    return () => {
      unsubscribe();
      setIsConnected(false);
    };
  }, [assessmentId, handleMessage]);

  return { status, message, isConnected };
}
